import React from 'react'
import { useTranslation } from 'react-i18next'
import { Col } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'

import TextInput from 'components/ui/forms/field/text-input/TextInput'
import { ILikerOptionModel } from 'models/questionLikert.model'
import styles from './LikertQuestion.module.scss'
import { MAX_OPTIONS, MIN_VISIBLE_OPTIONS } from './constants'
import {
  AddButton,
  Counter,
  Footer,
  OptionInput,
  OptionRow,
  RemoveButton,
  RemovePlaceholder,
} from './styled'

interface ILikertQuestion {
  data: ILikerOptionModel[]
  onChange?: (options: ILikerOptionModel[]) => void
  /** Called with the id of a saved option the user removed. */
  onRemoveSaved?: (id: number) => void
}

const PREFIX = 'administration.surveyEditor.question'

/**
 * Option fields of a scale question: five to start, up to ten. Only the
 * options added after the first five can be removed; the first five may stay
 * empty, and empty options are not saved.
 */
const LikertQuestion: React.FC<ILikertQuestion> = ({
  data,
  onChange,
  onRemoveSaved,
}) => {
  const { t } = useTranslation()
  const options = data.slice(0, MAX_OPTIONS)
  const atMax = options.length >= MAX_OPTIONS

  const handleChange = (label: string, index: number) => {
    const editedData = options.slice()
    editedData[index] = { ...editedData[index], label }
    onChange?.(editedData)
  }

  const handleAdd = () => {
    if (atMax) return
    onChange?.([...options, { label: '' }])
  }

  const handleRemove = (index: number) => {
    const removed = options[index]
    if (removed?.id) onRemoveSaved?.(removed.id)
    onChange?.(options.filter((_, position) => position !== index))
  }

  return (
    <>
      {options.map((option, index) => {
        const position = index + 1
        const optionLabel = `${t(`${PREFIX}.option`)} ${position}`
        return (
          <Col lg={12} xl={7} key={'question' + index}>
            <OptionRow data-testid={`likert-option-${position}`}>
              <div className={styles.questionLabel}>{optionLabel}</div>
              <OptionInput>
                <TextInput
                  name={'question' + index}
                  placeHolder=""
                  label=""
                  value={option.label ?? ''}
                  onChange={(e) => handleChange(e.target.value, index)}
                  error={''}
                />
              </OptionInput>
              {index >= MIN_VISIBLE_OPTIONS ? (
                <RemoveButton
                  type="button"
                  data-testid={`button-remove-likert-option-${position}`}
                  aria-label={t(`${PREFIX}.removeOption`, {
                    option: optionLabel,
                  })}
                  onClick={() => handleRemove(index)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </RemoveButton>
              ) : (
                <RemovePlaceholder />
              )}
            </OptionRow>
          </Col>
        )
      })}
      <Col lg={12} xl={7}>
        <Footer>
          <AddButton
            type="button"
            data-testid="button-add-likert-option"
            disabled={atMax}
            aria-disabled={atMax}
            onClick={handleAdd}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t(`${PREFIX}.addOption`)}
          </AddButton>
          <Counter
            data-testid="likert-options-counter"
            data-count={options.length}
            aria-live="polite"
          >
            {atMax
              ? t(`${PREFIX}.optionsLimitReached`, { max: MAX_OPTIONS })
              : t(`${PREFIX}.optionsCount`, {
                  count: options.length,
                  max: MAX_OPTIONS,
                })}
          </Counter>
        </Footer>
      </Col>
    </>
  )
}

export default LikertQuestion
